import React, { useState, useEffect } from 'react';
// Replaced react-icons with inline SVG components to match the vendor form
const CloseIcon = ({ size = 24, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
// New icons for maximize and minimize
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);
const PlusIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);
const TrashIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);
const API_URL = '/api';

// ---------- ErrorPopup component (paste here, right after imports) ----------
const ErrorPopup = ({ show, messages = [], onClose }) => {
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    if (!show) return;
    const timer = setTimeout(() => {
      try { containerRef.current?.querySelector('button')?.focus(); } catch {}
    }, 50);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
    };
  }, [show, onClose]);

  if (!show) return null;

  // Normalize any message types to strings
  const normalized = (Array.isArray(messages) ? messages : [messages]).map(m => {
    if (typeof m === 'string') return m;
    if (m instanceof Error) return m.message || String(m);
    if (m && typeof m === 'object') return m.message || JSON.stringify(m);
    return String(m);
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} />
      <div
        ref={containerRef}
        className="relative z-10 w-11/12 max-w-lg bg-white rounded-2xl shadow-2xl p-6 transform transition-all"
        role="document"
        aria-labelledby="error-popup-title"
      >
        <div className="flex items-center justify-center -mt-10 mb-3">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center shadow-sm">
            <svg className="w-8 h-8 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        </div>

        <div className="text-center mb-3">
          <h2 id="error-popup-title" className="text-xl font-semibold text-red-600">Error</h2>
          <p className="text-sm text-gray-600 mt-1">
            {normalized.length > 0 ? `${normalized.length} problem${normalized.length > 1 ? 's' : ''} found` : 'Something went wrong'}
          </p>
        </div>

        {/* Render ALL messages as pills, scrollable if many */}
        <div className="max-h-40 overflow-y-auto mb-3">
          <div className="flex flex-wrap gap-2 justify-center">
            {normalized.map((m, i) => (
              <span
                key={`pill-${i}`}
                className="text-xs px-3 py-1 bg-red-50 border border-red-100 text-red-700 rounded-full shadow-sm max-w-full truncate"
                title={m}
              >
                {m}
              </span>
            ))}
          </div>
        </div>

        <div className="flex justify-center items-center gap-3">
          
          <button onClick={onClose} className="px-5 py-2 bg-red-600 text-white rounded-full shadow hover:bg-red-700">Close</button>
        </div>
      </div>
    </div>
  );
};

const initialHeader = {
  rfq_no: '', rfq_date: '', pr_no: '', vendor_code: '', vendor_name: '', status: 'Draft', delivery_terms: '', payment_terms: '', department_id: ''
};
const initialItem = {
  item_code: '', description: '', quantity: '', unit_price: '', unit_of_measure: '', amount: '', discount_percent: '', discount: '', net_amount: '', department_id: ''
};

const RFQItemsFormModal = ({ open, onClose, onSubmit, initialData, isEdit }) => {
  const [header, setHeader] = useState(initialHeader);
  const [items, setItems] = useState([{ ...initialItem }]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  // Error popup state (add with other useState lines inside the component)
const [errorPopup, setErrorPopup] = useState({ show: false, messages: [] });

  
  // Department-related state
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const [showDepartmentOnHeader, setShowDepartmentOnHeader] = useState(false);
  const [showDepartmentOnItem, setShowDepartmentOnItem] = useState(false);
  
  // Function to fetch departments
  const fetchDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const response = await fetch(`${API_URL}/departments`);
      const data = await response.json();
      console.log('Fetched departments:', data);
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };
  
  // Function to fetch screen configuration
  const fetchScreenConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/screens`);
      const data = await response.json();
      console.log('Fetched screen configurations:', data);
      
      // Find the RFQ screen configuration
      const rfqScreen = data.screens.find(screen => 
        screen.ScreenName.toLowerCase() === 'RFQ'.toLowerCase()
      );
      
      console.log('RFQ screen configuration:', rfqScreen);
      if (rfqScreen) {
        setScreenConfig(rfqScreen);
        // Show department field on header if ShowOnHeader is true
        setShowDepartmentOnHeader(rfqScreen.ShowOnHeader === true);
        // Show department field on item if ShowOnItem is true
        setShowDepartmentOnItem(rfqScreen.ShowOnItem === true);
        
        // If department fields should be shown, fetch departments
        if (rfqScreen.ShowOnHeader === true || rfqScreen.ShowOnItem === true) {
          fetchDepartments();
        }
      }
    } catch (error) {
      console.error('Error fetching screen configuration:', error);
      setShowDepartmentOnHeader(false);
      setShowDepartmentOnItem(false);
    }
  };
  
  useEffect(() => {
    if (open) {
      // Fetch screen configuration when modal opens
      fetchScreenConfig();
      
      if (isEdit && initialData) {
        setHeader({ 
          ...initialHeader, 
          ...initialData,
          department_id: initialData.DepartmentID || ''
        });
        setItems(initialData.items && initialData.items.length > 0 
          ? initialData.items.map(item => ({
              ...item,
              department_id: item.DepartmentID || ''
            })) 
          : [{ ...initialItem }]
        );
      } else {
        setHeader(initialHeader);
        setItems([{ ...initialItem }]);
      }
      setErrors({});
      setIsSubmitting(false);
    } else {
      // Reset states when modal closes
      setShowDepartmentOnHeader(false);
      setShowDepartmentOnItem(false);
      setScreenConfig(null);
    }
  }, [open, initialData, isEdit]);
  
  const handleHeaderChange = e => {
    // Convert text input values to uppercase, but not for date or select fields
    if (e.target.tagName === 'INPUT' && e.target.type === 'text') {
      setHeader({ ...header, [e.target.name]: (e.target.value) });
    } else {
      setHeader({ ...header, [e.target.name]: e.target.value });
    }
  };
  
  const handleItemChange = (idx, e) => {
    const newItems = [...items];
    
    // Convert text input values to uppercase, but not for numeric fields
    if (e.target.tagName === 'INPUT' && e.target.type === 'text') {
      newItems[idx][e.target.name] = (e.target.value);
    } else {
      newItems[idx][e.target.name] = e.target.value;
    }
    
    // Auto-calc amount, discount, net_amount
    const q = parseFloat(newItems[idx].quantity) || 0;
    const up = parseFloat(newItems[idx].unit_price) || 0;
    const amt = q * up;
    newItems[idx].amount = amt.toFixed(2);
    const discP = parseFloat(newItems[idx].discount_percent) || 0;
    const disc = amt * (discP / 100);
    newItems[idx].discount = disc.toFixed(2);
    newItems[idx].net_amount = (amt - disc).toFixed(2);
    setItems(newItems);
  };
  
  const addItem = () => setItems([...items, { ...initialItem }]);
  const removeItem = idx => setItems(items.length > 1 ? items.filter((_, i) => i !== idx) : items);
  // Convert errors object (and header/items) into readable pill messages
const formatErrorKeyToMessage = (key, msg) => {
  // header keys (simple mapping)
  const headerMap = {
    rfq_no: 'RFQ No is required',
    rfq_date: 'RFQ Date is required',
    vendor_code: 'Vendor Code is required',
    vendor_name: 'Vendor Name is required',
    department_id: 'Department is required'
  };
  if (headerMap[key]) return headerMap[key];

  // item keys like "item_code_0", "description_2", "department_id_1"
  const match = key.match(/^([a-zA-Z_]+)_(\d+)$/);
  if (match) {
    const field = match[1];
    const index = parseInt(match[2], 10) + 1; // human-friendly 1-based
    const fieldMap = {
      item_code: 'Item Code',
      description: 'Description',
      quantity: 'Quantity',
      unit_price: 'Unit Price',
      unit_of_measure: 'UOM',
      department_id: 'Department'
    };
    const fname = fieldMap[field] || field;
    return `Item ${index}: ${fname} is required`;
  }

  // fallback
  return typeof msg === 'string' ? msg : `${key} - ${JSON.stringify(msg)}`;
};

const getAllErrorsFrom = (errsObj = {}, headerLocal = header, itemsLocal = items) => {
  const all = [];
  // header/field errors (top-level keys)
  Object.entries(errsObj).forEach(([k, v]) => {
    if (!v) return;
    // if the key looks like an item key we will format appropriately below
    all.push(formatErrorKeyToMessage(k, v));
  });
  return all;
};

  
  const validate = () => {
  const errs = {};
  if (!header.rfq_no || !header.rfq_no.trim()) errs.rfq_no = 'Required';
  if (!header.rfq_date) errs.rfq_date = 'Required';
  if (!header.vendor_code || !header.vendor_code.trim()) errs.vendor_code = 'Required';
  if (!header.vendor_name || !header.vendor_name.trim()) errs.vendor_name = 'Required';

  if (showDepartmentOnHeader && !header.department_id) errs.department_id = 'Department is required';

  items.forEach((item, idx) => {
    if (!item.item_code) errs[`item_code_${idx}`] = 'Required';
    if (!item.description) errs[`description_${idx}`] = 'Required';
    if (!item.quantity) errs[`quantity_${idx}`] = 'Required';
    if (!item.unit_price) errs[`unit_price_${idx}`] = 'Required';
    if (!item.unit_of_measure) errs[`unit_of_measure_${idx}`] = 'Required';
    if (showDepartmentOnItem && !item.department_id) errs[`department_id_${idx}`] = 'Department is required';
  });

  setErrors(errs);

  const valid = Object.keys(errs).length === 0;
  if (!valid) {
    const allErrors = getAllErrorsFrom(errs, header, items);
    setErrorPopup({ show: true, messages: allErrors });
  }
  return valid;
};

  
  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate() || isSubmitting) return;
    
    setIsSubmitting(true);
    setLoading(true);
    
    try {
      let rfqId = initialData?.id;
      
      // Build header payload explicitly to avoid missing/null fields, trim whitespace
      const headerPayload = {
        rfq_no: header.rfq_no ? header.rfq_no.trim() : '',
        rfq_date: header.rfq_date ? header.rfq_date : null,
        pr_no: header.pr_no ? header.pr_no.trim() : '',
        vendor_code: header.vendor_code ? header.vendor_code.trim() : '',
        vendor_name: header.vendor_name ? header.vendor_name.trim() : '',
        status: header.status,
        delivery_terms: header.delivery_terms ? header.delivery_terms.trim() : '',
        payment_terms: header.payment_terms ? header.payment_terms.trim() : '',
        updated_at: new Date()
      };
      
      // Add department_id to header payload if shown on header
      if (showDepartmentOnHeader) {
        headerPayload.department_id = header.department_id || null;
      }
      
      // Debug log
      console.log('Submitting RFQ header payload:', headerPayload);
      
      // Prevent submission if rfq_no is still empty after trim
      if (!headerPayload.rfq_no) {
        alert('RFQ No is required and cannot be empty.');
        return;
      }
      
      const headerRes = await fetch(`${API_URL}/rfqs/${rfqId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(headerPayload)
      });
      
      if (!headerRes.ok) {
        const errorData = await headerRes.json();
        throw new Error(errorData.message || 'Failed to update RFQ header');
      }
      
      // Prepare items payload with department_id if shown on items
      const itemsPayload = items.map(item => {
        const itemPayload = { ...item };
        
        // Add department_id to item payload if shown on items
        if (showDepartmentOnItem) {
          itemPayload.department_id = item.department_id || null;
        } else {
          // Remove department_id if not shown on items
          delete itemPayload.department_id;
        }
        
        return itemPayload;
      });
      
      // Update items
      const itemsRes = await fetch(`${API_URL}/rfqs/${rfqId}/items`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsPayload })
      });
      
      if (!itemsRes.ok) {
        const errorData = await itemsRes.json();
        throw new Error(errorData.message || 'Failed to update RFQ items');
      }
      
      // Call onSubmit to refresh the parent component
      if (onSubmit) {
        onSubmit();
      }
      
      // Close the modal after successful submission
      onClose();
      
    } catch (err) {
  // show server/network error in the same error popup
  const msg = err && err.message ? err.message : 'Error saving RFQ';
  console.error('Error saving RFQ:', err);
  setErrorPopup({ show: true, messages: [msg] });
} finally {

      setIsSubmitting(false);
      setLoading(false);
    }
  };
  
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  if (!open) return null;
  
  return (
    // Updated container to be full height and pinned to the right
    <div
      className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out
        ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
    >
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none; /* Internet Explorer 10+ */
            scrollbar-width: none; /* Firefox */
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none; /* Safari and Chrome */
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
          /* Style for uppercase text inputs */
          .uppercase-input {
            text-transform: uppercase;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      {/* This is the actual slide-in panel with fixed header and scrollable content */}
      <div 
        className={`bg-white shadow-2xl h-full flex flex-col
          transition-transform transform-gpu duration-300 ease-in-out
          ${open ? 'translate-x-0' : 'translate-x-full'}
          ${isMaximized ? 'w-full max-w-full' : 'w-full max-w-6xl'}`}
      >
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">{isEdit ? 'Edit RFQ' : 'Create RFQ'}</h2>
            <div className="flex items-center space-x-4">
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon size={24} /> : <MaximizeIcon size={24} />}
              </button>
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors" 
                onClick={onClose}
                title="Close"
              >
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Scrollable Content Area */}
       <div className="flex-grow overflow-y-auto custom-scrollbar px-8">
  <form onSubmit={handleSubmit} className="space-y-6 pb-6">
    
            {/* RFQ Header */}
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <h3 className="text-lg font-semibold text-[#384959] mb-4">RFQ Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">RFQ No *</label>
                  <input 
                    name="rfq_no" 
                    value={header.rfq_no} 
                    onChange={handleHeaderChange} 
                    className={`w-full border rounded-lg px-3 py-2 uppercase-input ${errors.rfq_no ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]`}
                  />
                  {errors.rfq_no && <span className="text-xs text-red-500">{errors.rfq_no}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">RFQ Date *</label>
                  <input 
                    type="date" 
                    name="rfq_date" 
                    value={header.rfq_date} 
                    onChange={handleHeaderChange} 
                    className={`w-full border rounded-lg px-3 py-2 ${errors.rfq_date ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]`}
                  />
                  {errors.rfq_date && <span className="text-xs text-red-500">{errors.rfq_date}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">PR No</label>
                  <input 
                    name="pr_no" 
                    value={header.pr_no} 
                    onChange={handleHeaderChange} 
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase-input"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Vendor Code *</label>
                  <input 
                    name="vendor_code" 
                    value={header.vendor_code} 
                    onChange={handleHeaderChange} 
                    className={`w-full border rounded-lg px-3 py-2 uppercase-input ${errors.vendor_code ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]`}
                  />
                  {errors.vendor_code && <span className="text-xs text-red-500">{errors.vendor_code}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Vendor Name *</label>
                  <input 
                    name="vendor_name" 
                    value={header.vendor_name} 
                    onChange={handleHeaderChange} 
                    className={`w-full border rounded-lg px-3 py-2 uppercase-input ${errors.vendor_name ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]`}
                  />
                  {errors.vendor_name && <span className="text-xs text-red-500">{errors.vendor_name}</span>}
                </div>

                  {/* Department Selection Field - Only shown if ShowOnHeader is true */}
                {showDepartmentOnHeader && (
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Department *</label>
                    <select 
                      name="department_id" 
                      value={header.department_id} 
                      onChange={handleHeaderChange} 
                      className={`w-full border rounded-lg px-3 py-2 ${errors.department_id ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase`}
                      disabled={loadingDepartments}
                    >
                      <option value="">{loadingDepartments ? 'Loading departments...' : 'Select Department'}</option>
                      {departments.map((dept) => (
                        <option key={dept.DepartmentID} value={dept.DepartmentID}>
                          {dept.DepartmentCode} - {dept.DepartmentName}
                        </option>
                      ))}
                    </select>
                    {errors.department_id && <span className="text-xs text-red-500">{errors.department_id}</span>}
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Status</label>
                  <select 
                    name="status" 
                    value={header.status} 
                    onChange={handleHeaderChange} 
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                  >
                    <option value="Draft">Draft</option>
                    <option value="Sent">Sent</option>
                    <option value="Closed">Closed</option>
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Delivery Terms</label>
                  <input 
                    name="delivery_terms" 
                    value={header.delivery_terms} 
                    onChange={handleHeaderChange} 
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase-input"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Payment Terms</label>
                  <input 
                    name="payment_terms" 
                    value={header.payment_terms} 
                    onChange={handleHeaderChange} 
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase-input"
                  />
                </div>
                
                
              </div>
            </div>
            
            {/* RFQ Items */}
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[#384959]">RFQ Items</h3>
                <button 
                  type="button" 
                  className="px-4 py-2 bg-[#6A89A7] text-white rounded-lg hover:bg-[#88BDF2] flex items-center transition-colors" 
                  onClick={addItem}
                >
                  <PlusIcon className="mr-1" /> Add Item
                </button>
              </div>
              <div className="overflow-x-auto custom-scrollbar">
                <table className="min-w-full border border-[#E2E8F0]">
                  <thead>
                    <tr className="bg-[#BDDDFC] text-[#384959]">
                      <th className="p-2">#</th>
                      <th className="p-2">Item Code</th>
                      <th className="p-2">Description</th>
                      <th className="p-2">Qty</th>
                      <th className="p-2">UOM</th>
                      <th className="p-2">Unit Price</th>
                      <th className="p-2">Amount</th>
                      <th className="p-2">Disc %</th>
                      <th className="p-2">Discount</th>
                      <th className="p-2">Net Amount</th>
                      {/* Add Department column if shown on items */}
                      {showDepartmentOnItem && <th className="p-2">Department</th>}
                      <th className="p-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={idx} className="border-t border-[#E2E8F0]">
                        <td className="p-2 text-center">{idx + 1}</td>
                        <td className="p-2">
                          <input 
                            name="item_code" 
                            value={item.item_code} 
                            onChange={e => handleItemChange(idx, e)} 
                            className={`w-24 border rounded-lg px-2 py-1 uppercase-input ${errors[`item_code_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                          />
                          {errors[`item_code_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        <td className="p-2">
                          <input 
                            name="description" 
                            value={item.description} 
                            onChange={e => handleItemChange(idx, e)} 
                            className={`w-32 border rounded-lg px-2 py-1 uppercase-input ${errors[`description_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                          />
                          {errors[`description_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        <td className="p-2">
                          <input 
                            name="quantity" 
                            type="number" 
                            min="0" 
                            value={item.quantity} 
                            onChange={e => handleItemChange(idx, e)} 
                            className={`w-16 border rounded-lg px-2 py-1 ${errors[`quantity_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                          />
                          {errors[`quantity_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        <td className="p-2">
                          <input 
                            name="unit_of_measure" 
                            value={item.unit_of_measure} 
                            onChange={e => handleItemChange(idx, e)} 
                            className={`w-16 border rounded-lg px-2 py-1 uppercase-input ${errors[`unit_of_measure_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                          />
                          {errors[`unit_of_measure_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        <td className="p-2">
                          <input 
                            name="unit_price" 
                            type="number" 
                            min="0" 
                            value={item.unit_price} 
                            onChange={e => handleItemChange(idx, e)} 
                            className={`w-20 border rounded-lg px-2 py-1 ${errors[`unit_price_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                          />
                          {errors[`unit_price_${idx}`] && <span className="text-xs text-red-500">*</span>}
                        </td>
                        <td className="p-2">
                          <input 
                            name="amount" 
                            value={item.amount} 
                            readOnly 
                            className="w-20 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                          />
                        </td>
                        <td className="p-2">
                          <input 
                            name="discount_percent" 
                            type="number" 
                            min="0" 
                            value={item.discount_percent} 
                            onChange={e => handleItemChange(idx, e)} 
                            className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
                          />
                        </td>
                        <td className="p-2">
                          <input 
                            name="discount" 
                            value={item.discount} 
                            readOnly 
                            className="w-20 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                          />
                        </td>
                        <td className="p-2">
                          <input 
                            name="net_amount" 
                            value={item.net_amount} 
                            readOnly 
                            className="w-20 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                          />
                        </td>
                        {/* Department column - Only shown if ShowOnItem is true */}
                        {showDepartmentOnItem && (
                          <td className="p-2">
                            <select 
                              name="department_id" 
                              value={item.department_id} 
                              onChange={e => handleItemChange(idx, e)} 
                              className={`w-32 border rounded-lg px-2 py-1 ${errors[`department_id_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase`}
                              disabled={loadingDepartments}
                            >
                              <option value="">Select Department</option>
                              {departments.map((dept) => (
                                <option key={dept.DepartmentID} value={dept.DepartmentID}>
                                  {dept.DepartmentCode}
                                </option>
                              ))}
                            </select>
                            {errors[`department_id_${idx}`] && <span className="text-xs text-red-500">*</span>}
                          </td>
                        )}
                        <td className="p-2 text-center">
                          <button 
                            type="button" 
                            className="text-red-500 hover:text-red-700 transition-colors" 
                            onClick={() => removeItem(idx)} 
                            disabled={items.length === 1}
                          >
                            <TrashIcon />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </form>
        </div>
        
        {/* Fixed Footer with Buttons */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button 
              type="button" 
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed" 
              disabled={loading || isSubmitting}
              onClick={handleSubmit}
            >
              {loading || isSubmitting ? 'Saving...' : (isEdit ? 'Update RFQ' : 'Save RFQ')}
            </button>
          </div>
        </div>
      </div>
      {/* Error popup */}
<ErrorPopup
  show={errorPopup.show}
  messages={errorPopup.messages}
  onClose={() => setErrorPopup({ show: false, messages: [] })}
/>

    </div>
  );
};
export default RFQItemsFormModal;